import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Trash2, Plus, RefreshCw, Search, ToggleLeft, ToggleRight,
  X, LogOut, AlertTriangle, CheckCircle, Bell, TrendingUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const CATEGORIES = ["Innovation", "Global", "Regional", "Shares/Stocks", "Horoscope", "Community"];

// ── Shared helpers ──────────────────────────────────────────────────────────

function SectionBtn({ id, label, active, onClick }) {
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`admin-section-${id}`}
      className={`font-sans text-[10px] uppercase tracking-[0.15em] font-semibold px-4 py-2 border transition-all whitespace-nowrap ${
        active ? "bg-black text-white border-black" : "border-[#E0E0E0] text-neutral-500 hover:border-black"
      }`}
    >
      {label}
    </button>
  );
}

// ── Section: User Management ─────────────────────────────────────────────────

function UserSection() {
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const handleDelete = () => {
    if (!email.trim()) return;
    setShowConfirm(true);
    setSuccessMsg("");
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/users`, {
        data: { email: email.trim() },
        withCredentials: true,
      });
      const deleted = email.trim();
      setSuccessMsg(`${deleted} has been deleted successfully.`);
      setEmail("");
      setConfirmEmail("");
      setShowConfirm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-light tracking-wider mb-2">User Management</h2>
      <p className="font-sans text-xs text-neutral-500 mb-6">
        Search a user by email and permanently delete their account along with all bookmarks, topics, and reports.
      </p>

      {successMsg && (
        <div
          data-testid="admin-user-success-msg"
          className="flex items-center gap-3 border border-green-200 bg-green-50 px-5 py-3 mb-5"
        >
          <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
          <p className="font-sans text-sm text-green-700 font-medium">{successMsg}</p>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <input
          value={email}
          onChange={e => { setEmail(e.target.value); setShowConfirm(false); setSuccessMsg(""); }}
          placeholder="user@email.com"
          data-testid="admin-user-email-input"
          className="border border-[#E0E0E0] px-3 py-2 font-sans text-sm flex-1 min-w-64 focus:outline-none focus:border-black"
          onKeyDown={e => e.key === "Enter" && handleDelete()}
        />
        <button
          onClick={handleDelete}
          disabled={!email.trim() || loading}
          data-testid="admin-user-delete-btn"
          className="bg-red-600 text-white px-5 py-2 font-sans text-[10px] uppercase tracking-[0.15em] font-semibold hover:bg-red-700 disabled:opacity-40 flex items-center gap-1.5 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete User
        </button>
      </div>

      {showConfirm && (
        <div className="border border-red-200 bg-red-50 px-5 py-4 mb-4">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-sans text-sm font-semibold text-red-700 mb-1">Permanent deletion</p>
              <p className="font-sans text-xs text-red-600">
                This will permanently delete <strong>{email}</strong> and all their data. This cannot be undone.
                Type the email below to confirm.
              </p>
            </div>
          </div>
          <input
            value={confirmEmail}
            onChange={e => setConfirmEmail(e.target.value)}
            placeholder={email}
            data-testid="admin-user-confirm-input"
            className="border border-red-300 px-3 py-2 font-sans text-sm w-full mb-3 focus:outline-none focus:border-red-500 bg-white"
          />
          <div className="flex gap-2">
            <button
              onClick={confirmDelete}
              disabled={confirmEmail.trim().toLowerCase() !== email.trim().toLowerCase() || loading}
              data-testid="admin-user-confirm-delete-btn"
              className="bg-red-600 text-white px-4 py-2 font-sans text-[10px] uppercase tracking-[0.15em] font-semibold disabled:opacity-40 hover:bg-red-700 transition-colors"
            >
              {loading ? "Deleting…" : "Yes, Delete Permanently"}
            </button>
            <button
              onClick={() => { setShowConfirm(false); setConfirmEmail(""); }}
              className="border border-[#E0E0E0] px-4 py-2 font-sans text-[10px] uppercase tracking-[0.15em] font-semibold hover:border-black transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: Article Removal ─────────────────────────────────────────────────

function ArticleSection() {
  const [value, setValue] = useState("");
  const [mode, setMode] = useState("url");
  const [loading, setLoading] = useState(false);

  const handleRemove = async () => {
    if (!value.trim()) return;
    setLoading(true);
    try {
      const payload = mode === "url" ? { url: value.trim() } : { id: value.trim() };
      await axios.delete(`${API}/admin/articles`, { data: payload, withCredentials: true });
      toast.success("Article removed from feed.");
      setValue("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Article not found or removal failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-light tracking-wider mb-2">Article Removal</h2>
      <p className="font-sans text-xs text-neutral-500 mb-6">
        Remove a specific article from the feed by its URL or MongoDB ID.
      </p>
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="flex border border-[#E0E0E0]">
          {["url", "id"].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-2 font-sans text-[10px] uppercase tracking-[0.15em] font-semibold transition-colors ${
                mode === m ? "bg-black text-white" : "text-neutral-500 hover:bg-neutral-50"
              }`}
            >
              By {m.toUpperCase()}
            </button>
          ))}
        </div>
        <input
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={mode === "url" ? "https://example.com/article-slug" : "MongoDB ObjectId (24 hex chars)"}
          data-testid="admin-article-input"
          className="border border-[#E0E0E0] px-3 py-2 font-sans text-sm flex-1 min-w-64 focus:outline-none focus:border-black"
          onKeyDown={e => e.key === "Enter" && handleRemove()}
        />
        <button
          onClick={handleRemove}
          disabled={!value.trim() || loading}
          data-testid="admin-article-remove-btn"
          className="bg-black text-white px-5 py-2 font-sans text-[10px] uppercase tracking-[0.15em] font-semibold hover:bg-neutral-800 disabled:opacity-40 flex items-center gap-1.5 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" /> {loading ? "Removing…" : "Remove"}
        </button>
      </div>
    </div>
  );
}

// ── Section: Boolean Search Tester ───────────────────────────────────────────

const DEFAULT_TERM = { text: "", op: "AND" };

function SearchSection() {
  const [terms, setTerms] = useState([{ text: "", op: "AND" }]);
  const [category, setCategory] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const addTerm = () => setTerms(p => [...p, { ...DEFAULT_TERM }]);
  const removeTerm = i => setTerms(p => p.filter((_, idx) => idx !== i));
  const updateTerm = (i, field, val) =>
    setTerms(p => p.map((t, idx) => (idx === i ? { ...t, [field]: val } : t)));

  const runSearch = async () => {
    const active = terms.filter(t => t.text.trim());
    if (!active.length) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/search`, { terms: active, category }, { withCredentials: true });
      setResults(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed.");
    } finally {
      setLoading(false);
    }
  };

  const opColors = {
    AND: "bg-blue-50 text-blue-700",
    OR: "bg-amber-50 text-amber-700",
    NOT: "bg-red-50 text-red-600",
  };

  return (
    <div>
      <h2 className="text-2xl font-light tracking-wider mb-2">Boolean Search Tester</h2>
      <p className="font-sans text-xs text-neutral-500 mb-6">
        Build a query using AND / OR / NOT logic and test it against the live article database.
      </p>
      <div className="space-y-2 mb-4">
        {terms.map((term, i) => (
          <div key={i} className="flex items-center gap-2">
            {i === 0 ? (
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-300 w-12 text-center shrink-0">WHERE</span>
            ) : (
              <select
                value={term.op}
                onChange={e => updateTerm(i, "op", e.target.value)}
                className={`font-mono text-[10px] uppercase tracking-widest font-bold w-14 border-0 py-2 focus:outline-none shrink-0 text-center cursor-pointer ${opColors[term.op]}`}
              >
                <option value="AND">AND</option>
                <option value="OR">OR</option>
                <option value="NOT">NOT</option>
              </select>
            )}
            <input
              value={term.text}
              onChange={e => updateTerm(i, "text", e.target.value)}
              placeholder={`Term ${i + 1}`}
              data-testid={`admin-search-term-${i}`}
              className="border border-[#E0E0E0] px-3 py-2 font-sans text-sm flex-1 focus:outline-none focus:border-black"
              onKeyDown={e => e.key === "Enter" && runSearch()}
            />
            {terms.length > 1 && (
              <button onClick={() => removeTerm(i)} className="text-neutral-300 hover:text-red-500 transition-colors shrink-0">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={addTerm}
          className="border border-[#E0E0E0] px-3 py-2 font-sans text-[10px] uppercase tracking-[0.15em] font-semibold hover:border-black transition-colors flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> Add Term
        </button>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="border border-[#E0E0E0] px-3 py-2 font-sans text-xs focus:outline-none focus:border-black"
        >
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          onClick={runSearch}
          disabled={!terms.some(t => t.text.trim()) || loading}
          data-testid="admin-search-run-btn"
          className="bg-black text-white px-5 py-2 font-sans text-[10px] uppercase tracking-[0.15em] font-semibold hover:bg-neutral-800 disabled:opacity-40 flex items-center gap-1.5 transition-colors ml-auto"
        >
          <Search className="w-3.5 h-3.5" /> {loading ? "Searching…" : "Run Search"}
        </button>
      </div>
      {results && (
        <div>
          <p className="font-sans text-xs text-neutral-500 mb-3">
            {results.count} match{results.count !== 1 ? "es" : ""} — showing first {results.articles.length}
          </p>
          {results.articles.length === 0 ? (
            <p className="font-sans text-xs text-neutral-400 py-4 border border-[#E0E0E0] text-center">No articles matched this query.</p>
          ) : (
            <div className="space-y-1">
              {results.articles.map(a => (
                <div key={a.id} className="border border-[#E0E0E0] px-4 py-3 bg-white hover:border-neutral-400 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <a href={a.url} target="_blank" rel="noopener noreferrer"
                        className="font-sans text-sm hover:underline line-clamp-1">{a.title}</a>
                      <p className="font-sans text-[10px] text-neutral-400 mt-0.5">
                        {a.source} · {a.category} · {a.published?.slice(0, 10)}
                      </p>
                    </div>
                    <span className="font-sans text-[9px] uppercase tracking-wider text-neutral-300 shrink-0">{a.id}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section: Source Toggle ───────────────────────────────────────────────────

function SourcesSection() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [toggling, setToggling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/sources`, { withCredentials: true });
      setSources(res.data.sources || []);
    } catch {
      toast.error("Failed to load sources.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (source, currentEnabled) => {
    setToggling(source);
    try {
      await axios.post(`${API}/admin/sources/toggle`, { source, enabled: !currentEnabled }, { withCredentials: true });
      setSources(p => p.map(s => s.source === source ? { ...s, enabled: !currentEnabled } : s));
      toast.success(`${source} ${!currentEnabled ? "enabled" : "disabled"}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed.");
    } finally {
      setToggling(null);
    }
  };

  const purge = async (source) => {
    if (!window.confirm(`Delete ALL stored articles from "${source}"? This cannot be undone.`)) return;
    try {
      const res = await axios.post(`${API}/admin/sources/purge`, { source }, { withCredentials: true });
      toast.success(`Purged ${res.data.deleted} article(s) from ${source}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed.");
    }
  };

  const filtered = sources.filter(s => {
    const q = filter.toLowerCase();
    return (!q || s.source.toLowerCase().includes(q) || s.url.toLowerCase().includes(q))
      && (!catFilter || s.category === catFilter);
  });

  const enabledCount = sources.filter(s => s.enabled).length;

  return (
    <div>
      <h2 className="text-2xl font-light tracking-wider mb-2">Source Toggle</h2>
      <p className="font-sans text-xs text-neutral-500 mb-1">
        Enable or disable individual RSS sources. Disabled sources are skipped during ingestion. Use <strong>Purge</strong> to also delete existing stored articles for a source.
      </p>
      <p className="font-sans text-[10px] text-neutral-400 mb-6">
        {enabledCount} of {sources.length} sources active
      </p>
      <div className="flex gap-2 mb-4 flex-wrap">
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search sources…"
          data-testid="admin-sources-filter"
          className="border border-[#E0E0E0] px-3 py-2 font-sans text-xs flex-1 min-w-48 focus:outline-none focus:border-black"
        />
        <select
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
          className="border border-[#E0E0E0] px-3 py-2 font-sans text-xs focus:outline-none focus:border-black"
        >
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={load} className="p-2 text-neutral-400 hover:text-black transition-colors border border-[#E0E0E0]">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {loading ? (
        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
      ) : (
        <div className="space-y-1">
          {filtered.map(s => (
            <div
              key={`${s.category}:${s.source}`}
              className={`flex items-center justify-between border px-4 py-3 bg-white transition-colors ${
                s.enabled ? "border-[#E0E0E0] hover:border-neutral-400" : "border-[#E0E0E0] bg-neutral-50 opacity-60"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-sans text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 bg-neutral-100 text-neutral-500 shrink-0">
                  {s.category}
                </span>
                <span className="font-sans text-sm truncate">{s.source}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-4">
                {!s.enabled && (
                  <button
                    onClick={() => purge(s.source)}
                    data-testid={`admin-source-purge-${s.source.replace(/\s/g, "-")}`}
                    title="Delete all stored articles for this source"
                    className="font-sans text-[9px] uppercase tracking-[0.12em] border border-red-200 text-red-500 hover:bg-red-50 px-2 py-1 transition-colors"
                  >
                    Purge
                  </button>
                )}
                <button
                  onClick={() => toggle(s.source, s.enabled)}
                  disabled={toggling === s.source}
                  data-testid={`admin-source-toggle-${s.source.replace(/\s/g, "-")}`}
                  className="transition-colors"
                  title={s.enabled ? "Click to disable" : "Click to enable"}
                >
                  {s.enabled
                    ? <ToggleRight className="w-6 h-6 text-black" />
                    : <ToggleLeft className="w-6 h-6 text-neutral-300" />}
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="font-sans text-xs text-neutral-400 py-6 text-center">No sources match your filter.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section: Keyword Manager ──────────────────────────────────────────────────

const KW_TABS = ["Innovation", "Global", "Regional", "Stocks", "Horoscope", "Community"];

function KeywordsSection() {
  const [keywords, setKeywords] = useState([]);
  const [tab, setTab] = useState("Innovation");
  const [keyword, setKeyword] = useState("");
  const [mode, setMode] = useState("boost");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/keywords`, { withCredentials: true });
      setKeywords(res.data.keywords || []);
    } catch {
      toast.error("Failed to load keywords.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const kw = keyword.trim();
    if (!kw) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/keywords`,
        { tab, keyword: kw, mode },
        { withCredentials: true }
      );
      // Append directly from server response — no second GET needed
      setKeywords(prev => [...prev, res.data]);
      setKeyword("");
      toast.success(`Keyword "${kw}" added.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save keyword.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/keywords/${id}`, { withCredentials: true });
      setKeywords(p => p.filter(k => k.id !== id));
      toast.success("Keyword removed.");
    } catch {
      toast.error("Failed to remove keyword.");
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-light tracking-wider mb-2">Keyword Manager</h2>
      <p className="font-sans text-xs text-neutral-500 mb-6">Boost or filter incoming RSS content per tab.</p>

      <div className="flex gap-2 mb-6 flex-wrap">
        <select
          value={tab}
          onChange={e => setTab(e.target.value)}
          className="border border-[#E0E0E0] px-3 py-2 font-sans text-xs focus:outline-none focus:border-black"
        >
          {KW_TABS.map(t => <option key={t}>{t}</option>)}
        </select>
        <input
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && add()}
          placeholder="Keyword or phrase"
          data-testid="admin-kw-input"
          className="border border-[#E0E0E0] px-3 py-2 font-sans text-xs flex-1 min-w-48 focus:outline-none focus:border-black"
        />
        <select
          value={mode}
          onChange={e => setMode(e.target.value)}
          className="border border-[#E0E0E0] px-3 py-2 font-sans text-xs focus:outline-none focus:border-black"
        >
          <option value="boost">Boost</option>
          <option value="filter">Filter Out</option>
        </select>
        <button
          onClick={add}
          disabled={!keyword.trim() || saving}
          data-testid="admin-kw-add-btn"
          className="bg-black text-white px-4 py-2 font-sans text-[10px] uppercase tracking-[0.15em] font-semibold hover:bg-neutral-800 disabled:opacity-40 flex items-center gap-1 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Add"}
        </button>
      </div>

      {loading ? (
        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
      ) : keywords.length === 0 ? (
        <p className="font-sans text-xs text-neutral-400">No keywords configured.</p>
      ) : (
        <div className="space-y-1">
          {keywords.map(kw => (
            <div key={kw.id} className="flex items-center justify-between border border-[#E0E0E0] px-4 py-3 bg-white hover:border-neutral-400 transition-colors">
              <div className="flex items-center gap-3">
                <span className="font-sans text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 bg-black text-white">{kw.tab}</span>
                <span className="font-sans text-sm">{kw.keyword}</span>
                <span className={`font-sans text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 ${kw.mode === "boost" ? "text-green-700 bg-green-50" : "text-red-700 bg-red-50"}`}>
                  {kw.mode}
                </span>
              </div>
              <button onClick={() => remove(kw.id)} className="text-neutral-300 hover:text-red-500 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Section: Content Reports ──────────────────────────────────────────────────

function ReportsSection() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/reports`, { withCredentials: true })
      .then(r => setReports(r.data.reports || []))
      .catch(() => toast.error("Failed to load reports."))
      .finally(() => setLoading(false));
  }, []);

  const removeArticle = async (report) => {
    setActing(report._id + "-remove");
    try {
      await axios.delete(`${API}/admin/articles`, {
        data: { url: report.article_url },
        withCredentials: true,
      });
      // Also dismiss the report
      await axios.delete(`${API}/admin/reports/${report._id}`, { withCredentials: true });
      setReports(p => p.filter(r => r._id !== report._id));
      toast.success("Article removed and report dismissed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove article.");
    } finally {
      setActing(null);
    }
  };

  const dismissReport = async (id) => {
    setActing(id + "-dismiss");
    try {
      await axios.delete(`${API}/admin/reports/${id}`, { withCredentials: true });
      setReports(p => p.filter(r => r._id !== id));
      toast.success("Report dismissed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to dismiss report.");
    } finally {
      setActing(null);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-light tracking-wider mb-6">Content Reports ({reports.length})</h2>
      {loading ? (
        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
      ) : reports.length === 0 ? (
        <p className="font-sans text-xs text-neutral-400">No reports yet.</p>
      ) : (
        <div className="space-y-2">
          {reports.map(r => (
            <div key={r._id} data-testid={`admin-report-${r._id}`} className="border border-[#E0E0E0] px-4 py-3 bg-white hover:border-neutral-400 transition-colors">
              <div className="flex justify-between items-start gap-4">
                <a
                  href={r.article_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sans text-sm hover:underline line-clamp-1 flex-1"
                >
                  {r.article_title || r.article_url}
                </a>
                <span className="font-sans text-[10px] text-neutral-400 shrink-0">{r.reported_at?.slice(0, 10)}</span>
              </div>
              <p className="font-sans text-[10px] text-neutral-500 mt-1">
                Reported by: {r.user_email}
                {r.reason ? ` · Reason: ${r.reason}` : ""}
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => removeArticle(r)}
                  disabled={!!acting}
                  data-testid={`admin-report-remove-${r._id}`}
                  className="font-sans text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 bg-black text-white hover:bg-neutral-800 disabled:opacity-40 flex items-center gap-1 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  {acting === r._id + "-remove" ? "Removing…" : "Remove Article"}
                </button>
                <button
                  onClick={() => dismissReport(r._id)}
                  disabled={!!acting}
                  data-testid={`admin-report-dismiss-${r._id}`}
                  className="font-sans text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 border border-[#E0E0E0] text-neutral-600 hover:border-black disabled:opacity-40 flex items-center gap-1 transition-colors"
                >
                  <X className="w-3 h-3" />
                  {acting === r._id + "-dismiss" ? "Dismissing…" : "Dismiss"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Section: Source Suggestions ───────────────────────────────────────────────

const SUGGESTION_CATEGORIES = ["Global", "Innovation", "Regional", "Shares/Stocks", "Horoscope", "Community"];
const REGIONAL_COUNTRIES = ["India", "Pakistan", "Bangladesh", "Sri Lanka", "USA", "UK", "Canada", "Australia", "Germany", "France", "Japan", "Singapore", "UAE"];

function SuggestionsSection() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  // Track which suggestion has the "add" form open and its chosen category/country
  const [addingId, setAddingId] = useState(null);
  const [addCategory, setAddCategory] = useState("Global");
  const [addCountry, setAddCountry] = useState("India");

  useEffect(() => {
    axios.get(`${API}/admin/suggestions`, { withCredentials: true })
      .then(r => setSuggestions(r.data.suggestions || []))
      .catch(() => toast.error("Failed to load suggestions."))
      .finally(() => setLoading(false));
  }, []);

  const openAddForm = (id) => {
    setAddingId(id);
    setAddCategory("Global");
    setAddCountry("India");
  };

  const confirmAddToSources = async (s) => {
    setActing(s._id + "-add");
    try {
      const payload = {
        name: s.publication_name || s.url,
        url: s.url,
        category: addCategory,
        suggestion_id: s._id,
      };
      if (addCategory === "Regional") payload.country = addCountry;
      await axios.post(`${API}/admin/sources/add`, payload, { withCredentials: true });
      setSuggestions(p => p.filter(sg => sg._id !== s._id));
      setAddingId(null);
      const label = addCategory === "Regional" ? `${addCategory} (${addCountry})` : addCategory;
      toast.success(`"${s.publication_name || s.url}" added to ${label} sources.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add source.");
    } finally {
      setActing(null);
    }
  };

  const dismissSuggestion = async (id) => {
    setActing(id + "-dismiss");
    try {
      await axios.delete(`${API}/admin/suggestions/${id}`, { withCredentials: true });
      setSuggestions(p => p.filter(s => s._id !== id));
      toast.success("Suggestion dismissed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to dismiss suggestion.");
    } finally {
      setActing(null);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-light tracking-wider mb-6">Source Suggestions ({suggestions.length})</h2>
      {loading ? (
        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
      ) : suggestions.length === 0 ? (
        <p className="font-sans text-xs text-neutral-400">No suggestions yet.</p>
      ) : (
        <div className="space-y-2">
          {suggestions.map(s => (
            <div key={s._id} data-testid={`admin-suggestion-${s._id}`} className="border border-[#E0E0E0] px-4 py-3 bg-white hover:border-neutral-400 transition-colors">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm font-medium truncate">{s.publication_name || "—"}</p>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sans text-[10px] text-neutral-400 hover:underline truncate block"
                  >
                    {s.url}
                  </a>
                </div>
                <span className="font-sans text-[10px] text-neutral-400 shrink-0">{s.submitted_at?.slice(0, 10)}</span>
              </div>
              <p className="font-sans text-[10px] text-neutral-500 mt-1">Submitted by: {s.user_email}</p>

              {addingId === s._id ? (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className="font-sans text-[10px] text-neutral-500">Add to:</span>
                  <select
                    value={addCategory}
                    onChange={e => setAddCategory(e.target.value)}
                    data-testid={`admin-suggestion-cat-${s._id}`}
                    className="border border-[#E0E0E0] px-2 py-1 font-sans text-xs focus:outline-none focus:border-black"
                  >
                    {SUGGESTION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {addCategory === "Regional" && (
                    <select
                      value={addCountry}
                      onChange={e => setAddCountry(e.target.value)}
                      data-testid={`admin-suggestion-country-${s._id}`}
                      className="border border-[#E0E0E0] px-2 py-1 font-sans text-xs focus:outline-none focus:border-black"
                    >
                      {REGIONAL_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                  <button
                    onClick={() => confirmAddToSources(s)}
                    disabled={!!acting}
                    data-testid={`admin-suggestion-confirm-add-${s._id}`}
                    className="font-sans text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 bg-black text-white hover:bg-neutral-800 disabled:opacity-40 transition-colors"
                  >
                    {acting === s._id + "-add" ? "Adding…" : "Confirm"}
                  </button>
                  <button
                    onClick={() => setAddingId(null)}
                    className="font-sans text-[10px] text-neutral-400 hover:text-black transition-colors px-2"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => openAddForm(s._id)}
                    disabled={!!acting}
                    data-testid={`admin-suggestion-add-${s._id}`}
                    className="font-sans text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 bg-black text-white hover:bg-neutral-800 disabled:opacity-40 flex items-center gap-1 transition-colors"
                  >
                    <Plus className="w-3 h-3" /> Add to Sources
                  </button>
                  <button
                    onClick={() => dismissSuggestion(s._id)}
                    disabled={!!acting}
                    data-testid={`admin-suggestion-dismiss-${s._id}`}
                    className="font-sans text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 border border-[#E0E0E0] text-neutral-600 hover:border-black disabled:opacity-40 flex items-center gap-1 transition-colors"
                  >
                    <X className="w-3 h-3" />
                    {acting === s._id + "-dismiss" ? "Dismissing…" : "Dismiss"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Root Admin Panel ──────────────────────────────────────────────────────────

// ── Section: Keyword Performance ─────────────────────────────────────────────
function KeywordPerformanceSection() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/keyword-performance`, { withCredentials: true });
      setData(res.data);
    } catch {
      toast.error("Could not load keyword performance data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl tracking-[0.1em] uppercase font-light text-[#1A1A1A] flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Keyword Performance
        </h2>
        <button onClick={load} disabled={loading}
          className="font-sans text-[10px] uppercase tracking-[0.18em] border border-[#E0E0E0] px-3 py-1.5 hover:border-black transition-colors flex items-center gap-1 disabled:opacity-40">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <p className="font-sans text-xs text-neutral-500 mb-6">
        Keywords that triggered the most push alerts in the last 24 hours, and keywords with zero matches.
      </p>
      {loading && <p className="font-sans text-xs text-neutral-400 animate-pulse">Loading…</p>}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A1A1A] mb-3 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 bg-black rounded-full" /> Hot Keywords
            </h3>
            {data.hot_keywords.length === 0 && (
              <p className="font-sans text-xs text-neutral-400">No alerts sent in the last 24 hours.</p>
            )}
            <div className="space-y-1.5">
              {data.hot_keywords.map((item, i) => (
                <div key={i} className="flex items-center justify-between border border-[#E0E0E0] bg-white px-3 py-2">
                  <span className="font-sans text-sm text-[#1A1A1A] font-medium">{item.keyword}</span>
                  <span className="font-sans text-xs font-bold text-[#1A1A1A] bg-neutral-100 px-2 py-0.5">
                    {item.count} alert{item.count !== 1 ? "s" : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400 mb-3 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 bg-neutral-300 rounded-full" /> Zero Alerts (24 h)
            </h3>
            {data.cold_keywords.length === 0 && (
              <p className="font-sans text-xs text-neutral-400">All keywords triggered at least one alert.</p>
            )}
            <div className="flex flex-wrap gap-2">
              {data.cold_keywords.map((kw, i) => (
                <span key={i} className="font-sans text-[10px] uppercase tracking-[0.1em] px-2.5 py-1 border border-neutral-200 text-neutral-500 bg-white">
                  {kw}
                </span>
              ))}
            </div>
            <p className="font-sans text-[10px] text-neutral-400 mt-4 leading-relaxed">
              Keywords with no alerts may be too narrow, misspelled, or simply had no matching articles today.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Section: Push Notifications ──────────────────────────────────────────────

function PushSection() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firing, setFiring] = useState(false);
  const [result, setResult] = useState(null);

  const loadSubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/push/subscriptions`, { withCredentials: true });
      setSubs(res.data.subscriptions || []);
    } catch {
      toast.error("Could not load subscriptions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  const fireTest = async () => {
    setFiring(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/push/test`, {}, { withCredentials: true });
      setResult(res.data);
      toast.success(`Test sent to ${res.data.sent} subscriber(s).`);
      loadSubs();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send test notification.";
      toast.error(msg);
    } finally {
      setFiring(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl tracking-[0.1em] uppercase font-light text-[#1A1A1A] flex items-center gap-2">
          <Bell className="w-4 h-4" /> Push Notifications
        </h2>
        <button
          onClick={loadSubs}
          disabled={loading}
          className="font-sans text-[10px] uppercase tracking-[0.18em] border border-[#E0E0E0] px-3 py-1.5 hover:border-black transition-colors flex items-center gap-1 disabled:opacity-40"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <p className="font-sans text-xs text-neutral-500 mb-6">
        View active push subscriptions and fire a test notification to all subscribers.
      </p>

      <div className="mb-4 p-4 border border-[#E0E0E0] bg-white flex items-center justify-between">
        <div>
          <span className="font-sans text-sm font-semibold text-[#1A1A1A]">{subs.length}</span>
          <span className="font-sans text-xs text-neutral-500 ml-2">active subscriber{subs.length !== 1 ? "s" : ""}</span>
        </div>
        <button
          data-testid="admin-push-test-btn"
          onClick={fireTest}
          disabled={firing || subs.length === 0}
          className="font-sans text-[10px] uppercase tracking-[0.18em] font-semibold bg-black text-white px-4 py-2 hover:bg-[#333] transition-colors disabled:opacity-40 flex items-center gap-1"
        >
          <Bell className="w-3 h-3" /> {firing ? "Sending…" : "Send Test Notification"}
        </button>
      </div>

      {result && (
        <div className="mb-4 p-3 border border-green-200 bg-green-50 font-sans text-xs text-green-800">
          Sent: {result.sent} | Expired cleaned: {result.expired_cleaned} | Total attempted: {result.total}
        </div>
      )}

      {subs.length > 0 && (
        <div className="space-y-2">
          {subs.map((s, i) => (
            <div key={i} className="border border-[#E0E0E0] p-3 bg-white font-sans text-xs">
              <div className="text-[#1A1A1A] font-semibold mb-1">{s.user_email || s.user_id}</div>
              <div className="text-neutral-400 truncate">{s.endpoint}</div>
              <div className="text-neutral-400 mt-1">Created: {s.created_at ? s.created_at.slice(0, 19) : "—"}</div>
            </div>
          ))}
        </div>
      )}

      {!loading && subs.length === 0 && (
        <p className="font-sans text-xs text-neutral-400 mt-4">
          No active subscriptions. Ask users to click the bell icon to subscribe.
        </p>
      )}
    </div>
  );
}


const SECTIONS = [
  { id: "users",       label: "Users" },
  { id: "articles",    label: "Articles" },
  { id: "search",      label: "Search" },
  { id: "sources",     label: "Sources" },
  { id: "keywords",    label: "Keywords" },
  { id: "reports",     label: "Reports" },
  { id: "suggestions", label: "Suggestions" },
  { id: "push",        label: "Push" },
  { id: "kw-perf",     label: "KW Performance" },
];

export default function AdminPanel({ authUser, onBack, onLogout }) {
  const [section, setSection] = useState("users");

  return (
    <div className="min-h-screen bg-[#FAFAF8]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      {/* Header */}
      <div className="border-b border-[#E0E0E0] px-6 py-4 flex items-center justify-between bg-white">
        <div>
          <span className="text-2xl tracking-[0.2em] uppercase font-light text-[#1A1A1A]">Arvay</span>
          <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-neutral-400 ml-3">Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="font-sans text-[10px] uppercase tracking-[0.18em] font-semibold border border-[#E0E0E0] px-3 py-1.5 hover:border-black transition-colors"
          >
            ← Dashboard
          </button>
          {onLogout && (
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="font-sans text-[10px] uppercase tracking-[0.18em] font-semibold border border-[#E0E0E0] px-3 py-1.5 hover:border-black transition-colors flex items-center gap-1"
            >
              <LogOut className="w-3 h-3" /> Logout
            </button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Section nav */}
        <div className="flex gap-1.5 mb-8 flex-wrap">
          {SECTIONS.map(s => (
            <SectionBtn key={s.id} id={s.id} label={s.label} active={section === s.id} onClick={setSection} />
          ))}
        </div>

        {section === "users"       && <UserSection />}
        {section === "articles"    && <ArticleSection />}
        {section === "search"      && <SearchSection />}
        {section === "sources"     && <SourcesSection />}
        {section === "keywords"    && <KeywordsSection />}
        {section === "reports"     && <ReportsSection />}
        {section === "suggestions" && <SuggestionsSection />}
        {section === "push"        && <PushSection />}
        {section === "kw-perf"     && <KeywordPerformanceSection />}
      </div>
    </div>
  );
}
